import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, post } from '../../lib/api.js';
import { useAuth, type PublicUser } from '../../lib/auth.js';
import { errorMessage, isPasskeyCancelled } from '../../lib/passkey.js';
import {
  startAuthentication,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { Alert, fmtDate, InviteLinkCard, Spinner, type InviteLink, PasswordField } from '../../lib/ui.js';
import { Modal } from '../../lib/Modal.js';
import { t } from '../../i18n/index.js';

type AdminRow = PublicUser & { passkey: { registered: boolean; since?: string } };
type Challenge = {
  method: 'otp' | 'passkey';
  sent_to?: string;
  expires_at?: string;
  attempts_allowed?: number;
};
type StepUp = Challenge & { admin: AdminRow };

export function AdminsPage() {
  const { me } = useAuth();
  const [admins, setAdmins] = useState<AdminRow[] | null>(null);
  const [link, setLink] = useState<{ link: InviteLink; note?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState<StepUp | null>(null);
  const [code, setCode] = useState('');
  const [stepError, setStepError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setAdmins((await api<{ admins: AdminRow[] }>('/admin/admins')).admins);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'email_taken'
          ? t('admin_email_taken')
          : errorMessage(err),
      );
    } finally {
      setBusy(false);
    }
  };

  const create = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const r = await post<{ admin: PublicUser; enrol: InviteLink }>('/admin/admins', form);
      setLink({ link: r.enrol, note: t('admin_created') });
      setAdding(false);
      setForm({ name: '', email: '', password: '' });
    });
  };

  const enrolLink = (a: AdminRow) =>
    void run(async () => {
      const r = await post<{ enrol: InviteLink }>(`/admin/admins/${a.id}/enrol-link`);
      setLink({ link: r.enrol, note: t('link_open_invalidates') });
    });

  /** Ask the server which proof it wants, then open the confirmation dialog. */
  const askDeactivate = async (a: AdminRow) => {
    setBusy(true);
    setError(null);
    setStepError(null);
    setCode('');
    try {
      const r = await post<Challenge>(`/admin/admins/${a.id}/deactivate/challenge`);
      setStep({ admin: a, ...r });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const finishDeactivate = async (body: Record<string, unknown>) => {
    if (!step) return;
    setBusy(true);
    setStepError(null);
    try {
      await post(`/admin/admins/${step.admin.id}/deactivate`, body);
      setStep(null);
      await load();
    } catch (err) {
      const left =
        err instanceof ApiError && typeof err.details.attempts_left === 'number'
          ? Number(err.details.attempts_left)
          : null;
      setStepError(
        left !== null
          ? `${errorMessage(err)} ${t('admins_code_attempts', { n: left })}`
          : errorMessage(err),
      );
    } finally {
      setBusy(false);
    }
  };

  /** The passkey flavour: a fresh assertion, then the same call. */
  const confirmWithPasskey = async () => {
    if (!step) return;
    setBusy(true);
    setStepError(null);
    try {
      const { challengeId, options } = await post<{
        challengeId: string;
        options: PublicKeyCredentialRequestOptionsJSON;
      }>(`/admin/admins/${step.admin.id}/deactivate/passkey-options`);
      const response = await startAuthentication({ optionsJSON: options });
      await finishDeactivate({ challengeId, response });
    } catch (err) {
      setStepError(isPasskeyCancelled(err) ? t('passkey_cancel_hint') : errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      {link && (
        <InviteLinkCard
          link={link.link}
          {...(link.note ? { note: link.note } : {})}
          onClose={() => setLink(null)}
        />
      )}
      <section className="card">
        <div className="row between">
          <h2>{t('admins_title')}</h2>
          <button type="button" onClick={() => setAdding((a) => !a)}>
            {t('admins_add')}
          </button>
        </div>
        {error && <Alert>{error}</Alert>}
        {adding && (
          <form onSubmit={create} className="grid-form">
            <label>
              {t('worker_name')}
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                minLength={2}
              />
            </label>
            <label>
              {t('email')}
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </label>
            <PasswordField
              id="new-admin-password"
              label={t('password')}
              hint={<span className="muted">{t('password_hint')}</span>}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={12}
              autoComplete="new-password"
            />
            <div className="row">
              <button type="submit" disabled={busy}>
                {t('save')}
              </button>
              <button type="button" className="secondary" onClick={() => setAdding(false)}>
                {t('cancel')}
              </button>
            </div>
          </form>
        )}
        {!admins ? (
          <Spinner />
        ) : (
          <div className="table-wrap">
            <table className="stacks">
              <thead>
                <tr>
                  <th>{t('worker_name')}</th>
                  <th>{t('email')}</th>
                  <th>{t('worker_passkey')}</th>
                  <th>{t('worker_status')}</th>
                  <th>{t('worker_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((a) => (
                  <tr key={a.id} className={a.active ? '' : 'inactive'}>
                    <td className="cell-name">{a.name}</td>
                    <td data-label={t('email')}>{a.email}</td>
                    <td data-label={t('worker_passkey')}>
                      {a.passkey.registered ? (
                        <span className="ok">
                          {t('passkey_registered_since', { date: fmtDate(a.passkey.since, false) })}
                        </span>
                      ) : (
                        <span className="muted">{t('passkey_not_registered')}</span>
                      )}
                    </td>
                    <td data-label={t('worker_status')}>
                      {a.active ? t('worker_active') : t('worker_inactive')}
                    </td>
                    <td className="actions">
                      <span className="row">
                        {a.active && (
                          <button
                            type="button"
                            className="small secondary"
                            onClick={() => enrolLink(a)}
                            disabled={busy}
                          >
                            {t('admin_new_enrol_link')}
                          </button>
                        )}
                        {a.active && a.id !== me?.user?.id && (
                          <button
                            type="button"
                            className="small danger"
                            onClick={() => void askDeactivate(a)}
                            disabled={busy}
                          >
                            {t('worker_deactivate')}
                          </button>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Modal
        open={!!step}
        title={t('admins_deactivate_title', { name: step?.admin.name ?? '' })}
        onClose={() => setStep(null)}
        footer={
          step?.method === 'otp' ? (
            <>
              <button type="button" className="secondary" onClick={() => setStep(null)}>
                {t('cancel')}
              </button>
              <button
                type="button"
                className="danger"
                disabled={busy || code.trim().length !== 6}
                onClick={() => void finishDeactivate({ code: code.trim() })}
              >
                {t('worker_deactivate')}
              </button>
            </>
          ) : (
            <>
              <button type="button" className="secondary" onClick={() => setStep(null)}>
                {t('cancel')}
              </button>
              <button
                type="button"
                className="danger"
                disabled={busy}
                onClick={() => void confirmWithPasskey()}
              >
                {t('admins_confirm_passkey')}
              </button>
            </>
          )
        }
      >
        <p>{t('admins_deactivate_body')}</p>
        {step?.method === 'otp' ? (
          <>
            <p className="muted">{t('admins_code_sent', { email: step.sent_to ?? '' })}</p>
            <label>
              {t('admins_code_label')}
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="123456"
                data-autofocus=""
              />
            </label>
          </>
        ) : (
          <p className="muted">{t('admins_passkey_needed')}</p>
        )}
        {stepError && <Alert>{stepError}</Alert>}
      </Modal>
    </div>
  );
}
